import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, Clock, ArrowClockwise, RocketLaunch } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ICON = {
  pass: [CheckCircle, "text-[#2F6B3A]", "Done"],
  fail: [XCircle, "text-[#B3402A]", "Needs attention"],
  waiting: [Clock, "text-[#C58F28]", "Waiting"],
};

const CheckRow = ({ c }) => {
  const [Icon, cls, label] = ICON[c.status] || ICON.waiting;
  return (
    <li data-testid={`go-live-check-${c.key}`} className="flex gap-3 rounded-xl bg-white p-4 ring-1 ring-[#EBE4DA]">
      <Icon size={26} weight="fill" className={`shrink-0 ${cls}`} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-semibold text-[#2C2A28]">{c.title}</p>
          <span data-testid={`go-live-status-${c.key}`} className={`text-xs font-semibold uppercase tracking-wide ${cls}`}>{label}</span>
        </div>
        <p className="mt-1 break-words text-sm text-[#6E665E]">{c.detail}</p>
      </div>
    </li>
  );
};

export default function GoLiveChecklist() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => {
    setBusy(true);
    api.get("/admin/go-live").then((r) => { setData(r.data); setError(""); })
      .catch((e) => setError(e.response?.status === 403 ? "This page is for the site owner only." : "Couldn't run the checks. Please try again."))
      .finally(() => setBusy(false));
  };
  useEffect(load, []);
  const orderTest = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/go-live/order-test");
      (r.data.ok ? toast.success : toast.error)(r.data.detail, { duration: 9000 });
    } catch {
      toast.error("Couldn't run the order test.");
    }
    load();
  };

  return (
    <main data-testid="go-live-page" className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#E06D53]"><RocketLaunch size={16} weight="duotone" /> Owner only</p>
      <h1 className="mt-2 font-serif text-4xl text-[#2C2A28] sm:text-5xl">Go Live checklist</h1>
      <p className="mt-2 text-[#6E665E]">Checks this website's PayPal setup before real sales start. Secret values are never shown.</p>
      {error && <p data-testid="go-live-error" className="mt-6 rounded-xl bg-[#FDF7EB] p-4 text-[#4A4540]">{error}</p>}
      {data && (
        <>
          <div data-testid="go-live-summary" className={`mt-6 rounded-2xl p-5 ${data.ready || data.all_checks_pass ? "bg-[#EAF4EC]" : "bg-[#FFF4D6]"}`}>
            <p className="font-serif text-2xl text-[#2C2A28]">
              {data.ready ? "Ready for real sales"
                : data.locked && data.all_checks_pass ? "All checks passed. Waiting for your approval"
                : data.locked ? "Live keys loaded, sales locked"
                : data.mode === "live" ? "Almost there" : "Not live yet: payments are in Sandbox"}
            </p>
            <p className="mt-1 break-words text-sm text-[#4A4540]">
              {data.checks.filter((c) => c.status === "pass").length} of {data.checks.length} checks done for {data.site} · checked {new Date(data.checked_at).toLocaleTimeString("en-AU")}
            </p>
          </div>
          <div className="mt-5 space-y-6">
            {[...new Set(data.checks.map((c) => c.group))].map((g) => (
              <section key={g} data-testid={`go-live-group-${g.toLowerCase()}`}>
                <h2 className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#36858B]">{g}</h2>
                <ul className="space-y-3">{data.checks.filter((c) => c.group === g).map((c) => <CheckRow key={c.key} c={c} />)}</ul>
              </section>
            ))}
          </div>
        </>
      )}
      <div className="mt-6 flex flex-wrap gap-3">
        <button data-testid="go-live-refresh" onClick={load} disabled={busy}
          className="flex min-h-[44px] items-center gap-2 rounded-full bg-[#2C2A28] px-6 text-sm font-semibold text-white disabled:opacity-60">
          <ArrowClockwise size={16} className={busy ? "animate-spin" : ""} /> {busy ? "Checking…" : "Run checks again"}
        </button>
        <button data-testid="go-live-order-test" onClick={orderTest} disabled={busy}
          className="min-h-[44px] rounded-full border-2 border-[#E06D53] px-6 text-sm font-semibold text-[#E06D53] hover:bg-[#FFF1E8] disabled:opacity-60">
          Test order creation (no charge)
        </button>
        <button data-testid="go-live-payments" onClick={() => navigate("/admin/payments")}
          className="min-h-[44px] rounded-full border-2 border-[#36858B] px-6 text-sm font-semibold text-[#36858B] hover:bg-[#EBF4F5]">
          Payment history
        </button>
      </div>
    </main>
  );
}
